import { Component, inject, OnDestroy, OnInit, HostListener, ElementRef, Renderer2 } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog'
import { WizardService } from '../services/wizard.service'
import {
  ADDRESS_INPUT,
  BOOLEAN_INPUT,
  CURRENCY_INPUT,
  DATE_INPUT,
  DROP_DOWN,
  FLOAT_INPUT,
  INTEGER_INPUT,
  GRID_INPUT,
  LABEL,
  MULTILINE_INPUT,
  PROGRESS,
  LOG,
  PUSH_UPDATE,
  REFERENCE,
  STRING_INPUT,
  WizardLayoutItem,
  WizardAction,
  WizardButton
} from '../models/wizard-data'
import { from, Observable, Subject } from 'rxjs'
import { filter, mergeMap, take, takeUntil, tap } from 'rxjs/operators'
import { WizardUtil } from '../wizard.util'
import { EntityService, NotificationService, TableData } from '@dot-it/jml-lib'
import { Address } from '../../../models/address'
import { DropDownEntry } from '../custom-inputs/wizard-drop-down/drop-down-entry.model'
import { ActionService } from '../../action-module/services/action.service'

class ButtonClick {
  type = 'BUTTON_CLICK'
  buttonId: string
}

class UpdateField {
  type: 'UPDATE_FIELD'
  fieldName: string
  value: any
}
type ServerEvent = ButtonClick | UpdateField

@Component({
  selector: 'jml-lib-wizard',
  templateUrl: './wizard.component.html',
  styleUrls: ['./wizard.component.scss']
})
export class WizardComponent implements OnInit, OnDestroy {
  protected readonly DROP_DOWN = DROP_DOWN
  protected readonly STRING_INPUT = STRING_INPUT
  protected readonly BOOLEAN_INPUT = BOOLEAN_INPUT
  protected readonly FLOAT_INPUT = FLOAT_INPUT
  protected readonly INTEGER_INPUT = INTEGER_INPUT
  protected readonly DATE_INPUT = DATE_INPUT
  protected readonly REFERENCE = REFERENCE
  protected readonly LABEL = LABEL
  protected readonly CURRENCY_INPUT = CURRENCY_INPUT
  protected readonly ADDRESS_INPUT = ADDRESS_INPUT
  protected readonly MULTILINE_INPUT = MULTILINE_INPUT
  protected readonly GRID_INPUT = GRID_INPUT
  protected readonly PROGRESS = PROGRESS
  protected readonly LOG = LOG

  scrollPanelStyle: any

  wizardForm: FormGroup

  wizardExecutorId: string

  entitySelectionContextId: string // the id of the button or process which requested the opening of the dialog (is needed to associate the result on the server side)
  entitySelectionDtoType: string
  entitySelectionHeader: string
  entitySelectionVisible = false
  entitySelectionTableData: TableData | undefined = undefined

  $serverEvents = new Subject<ServerEvent>()

  $destroy = new Subject<any>()
  destroy$ = this.$destroy.asObservable()

  layout$: Observable<WizardAction>

  controls = {}
  wzFields: WizardLayoutItem[] = []

  // Flag which prevent
  noMoreActions = false

  private entityService = inject(EntityService)
  private ref = inject(DynamicDialogRef)
  private config = inject(DynamicDialogConfig)
  private wizardService = inject(WizardService)
  private notificationService = inject(NotificationService)
  private actionService = inject(ActionService)
  private el = inject(ElementRef)
  private renderer = inject(Renderer2)

  // Hot Key support
  private hotkeyButtonMap: Map<string, string> = new Map()
  private focusedField?: string

  ngOnInit(): void {
    const wizardHrid = this.config.data.wizardHrid
    const dtoType = this.config.data.dtoType
    const entityId = this.config.data.entityId
    const fieldName = this.config.data.fieldName
    const wizType = this.config.data.wizType

    const executorId = this.config.data.executorId

    if (!this.config.header) {
      this.config.header = `Lade Wizard..`
    }

    const wizardAction$ = (
      !!executorId
        ? this.wizardService.getWizard(executorId)
        : this.wizardService.initWizard(wizardHrid, dtoType, entityId, fieldName)
    ).pipe(
      takeUntil(this.destroy$),
      tap((notification) => (this.wizardExecutorId = notification.id)),
      mergeMap((notification) => from(notification.actions))
    )

    this.layout$ = wizardAction$.pipe(filter((action) => action.type === 'LAYOUT'))

    wizardAction$.subscribe((action) => {
      // ('Wizard', wizard.action.type, wizard);

      if (action.type === 'CLOSE') {
        this.onCloseRequested()
      } else if (action.type === 'EXECUTE_ACTION') {
        this.executeAction(action.actionHrid, action.dtoType, action.entityId)
      } else if (action.type === 'EXECUTE_WIZARD') {
        this.executeWizard(action)
      } else if (action.type === 'MESSAGE') {
        this.notificationService.addNotification({
          message: action.message,
          type: action.messageType,
          delayInMS: action.delayInMS
        })
      } else if (action.type === 'UPDATE_FIELD') {
        const wzField = this.wzFields[action.field]
        if (!!wzField) {
          const value: any | null = this.mapInboundValue(wzField, action.value)
          const fc: FormControl = this.controls[action.field]
          fc.setValue(value)
        } else {
          console.error('Found no field', action.field)
        }
      } else if (action.type === 'OPEN_ENTITY_DIALOG') {
        this.openEntitySelectionDialog(action.contextId, action.dtoType, action.header)
      } else {
        // if (action.type === 'LAYOUT' || action.type === 'INIT')
        // assume update of structure only - we only consider the values, if the layout item did not exist before...
        // setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
          this.config.header = action.title

          if (!!action.width) {
            this.config.width = action.width
          }
        })

        this.generateRegistrationForm(this.wizardExecutorId, action.fields)
        this.updateHotkeyMap(action.buttons)
      }
    })

    this.onWindowResize()
    this.injectCloseButton()
  }

  executeAction(actionHrid: string, dtoType: string, entityId: number, contextId?: string) {
    console.log('Executing action:', actionHrid, 'for', dtoType, entityId)

    this.actionService.performAction(
      actionHrid,
      !!dtoType && !!entityId ? { dtoType, id: entityId } : undefined,
      undefined, // mainRef
      (success) => {
        // TODO, what do we want to do with the action result? Notify backend?
        console.log('Action execution result:', success)
      },
      true, // dialogClosable
      true // suppress Success toast
    )
  }

  executeWizard(action: WizardAction) {
    if (action.type === 'EXECUTE_WIZARD') {
      this.wizardService.openWizard(
        action.wizardExecutorId,
        action.wizardId,
        action.dtoType,
        action.entityId,
        undefined,
        undefined
      )
    }
  }

  onWindowResize() {
    this.scrollPanelStyle = { overflow: 'auto', width: '100%', 'max-height': window.innerHeight * 0.7 + 'px' }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      if (!this.isTopmostDialog()) {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      this.onRequestClose()

      return
    }

    // check if the event corresponds to a registered hotkey
    const key = this.normalizeKey(event)

    if (this.hotkeyButtonMap.has(key)) {
      event.preventDefault()

      // check if an element update is needed
      if (this.focusedField) {
        this.onUpdate(this.focusedField)
      }

      // run the button action
      this.onClickButton(this.hotkeyButtonMap.get(key))
    }
  }

  private normalizeKey(event: KeyboardEvent): string {
    // for simple function keys
    if (event.key.match(/^F\d+$/)) {
      return event.key.toLowerCase()
    }

    // for combinations with modifier keys
    const modifiers = []
    if (event.ctrlKey) modifiers.push('ctrl')
    if (event.altKey) modifiers.push('alt')
    if (event.shiftKey) modifiers.push('shift')
    if (event.metaKey) modifiers.push('cmd') // for Mac
    modifiers.push(event.key.toLowerCase())

    return modifiers.join('+')
  }

  private updateHotkeyMap(buttons: WizardButton[]) {
    this.hotkeyButtonMap.clear()

    if (buttons) {
      buttons.forEach((button) => {
        if (button.hotkey && !button.disabled) {
          this.hotkeyButtonMap.set(button.hotkey.toLowerCase(), button.id)
        }
      })
    }
  }

  generateRegistrationForm(executorId: string, fields: WizardLayoutItem[]): void {
    const oldControls = this.controls
    this.controls = {}
    this.wzFields = []
    fields.forEach((wzField) => {
      this.wzFields[wzField.field] = wzField

      if (oldControls.hasOwnProperty(wzField.field)) {
        this.controls[wzField.field] = oldControls[wzField.field]
      } else {
        // new Field - we need to create a new form control

        if (wzField.type === DROP_DOWN) {
          // For Dropdowns the backend sends just the id. But the WizardDropdown requires an DropDownEntry to be passed in.
          // Hence we get the entry here from the id and set the form control accordingly.
          const dropDownValue: DropDownEntry | null = this.mapInboundValue(wzField, wzField.value)
          this.controls[wzField.field] = new FormControl(dropDownValue ?? null)
        } else if (wzField.type === ADDRESS_INPUT) {
          // Use the generic method to create a form group for any complex input
          // TODO: What if wizard field does not have a value??
          if (WizardUtil.hasValue(wzField)) {
            const addressValue = wzField.value as Address
            // create formgroup from object keys
            const fg = this.createFormGroupFromObject(addressValue)
            this.controls[wzField.field] = fg
            this.wzFields[wzField.field] = wzField
          }
        } else if (wzField.type === GRID_INPUT) {
          this.controls[wzField.field] = new FormControl(wzField.value ?? [])
        } else {
          this.controls[wzField.field] = new FormControl(wzField.value ?? null)
        }
      }
    })
    // TODO: cleanup previous FormGroup
    this.wizardForm = new FormGroup(this.controls)
  }

  mapInboundValue(wzField: WizardLayoutItem, wzValue: any): any | null {
    if (wzField.type === DROP_DOWN) {
      return wzField['entries'].find((entry) => (<DropDownEntry>entry)?.id === wzValue)
    }
    return wzValue
  }

  createFormGroupFromObject(value: any): FormGroup {
    const group: { [key: string]: FormControl } = {}

    Object.keys(value).forEach((key) => {
      group[key] = new FormControl(value[key] ?? null) // Use value if defined, otherwise initialize with null
    })

    return new FormGroup(group)
  }

  getFormControl(fieldName: string): FormControl {
    return this.wizardForm.get(fieldName) as FormControl
  }

  getFloatPattern(decimals: number) {
    return decimals == null
      ? '^-?\\d{1,3}((\\.)?\\d{3})*(,\\d*)?$'
      : '^-?\\d{1,3}((\\.)?\\d{3})*(,\\d{1,' + decimals + '})?$'
  }

  onFocus(fieldName: string): void {
    console.log('onFocus', fieldName)
    this.focusedField = fieldName
  }

  onUpdate(fieldName: string) {
    this.focusedField = undefined
    setTimeout(() => {
      if (!this.noMoreActions) {
        const wizItem: WizardLayoutItem = this.wzFields[fieldName]
        if (wizItem.onModify === PUSH_UPDATE) {
          const wzFormValues = this.getProcessedFormValues()
          this.wizardService.pushUpdate(this.wizardExecutorId, fieldName, wzFormValues[fieldName], wzFormValues)
        }
      }
    }, 0)
  }

  onClickDelete(fieldName: string) {
    const control = this.getFormControl(fieldName)
    control.setValue(null)
    this.onUpdate(fieldName)
  }

  public submitWizardForm(): void {
    // if ( this.wizardForm.valid ) {
    //     const registrationRequest = this.getProcessedFormValues();
    //     // Success 🎉
    // }
  }

  private getProcessedFormValues(): any {
    // Create a copy of the current form values
    const wzFormValues = { ...this.wizardForm.value }

    // Iterate through each field and process dropdown values
    Object.keys(wzFormValues).forEach((fieldName) => {
      const wzField = this.wzFields[fieldName] // Retrieve the field metadata
      wzFormValues[fieldName] = this.transformedFieldValue(wzField, wzFormValues[fieldName])
    })

    return wzFormValues // Return the updated object
  }

  private transformedFieldValue(wzField: WizardLayoutItem, wzFieldValue: any): any | null {
    // For DROP_DOWN just pass the id of the selected entry
    if (wzField?.type === DROP_DOWN && wzFieldValue?.id) {
      return wzFieldValue.id
    }
    return wzFieldValue
  }

  private injectCloseButton(): void {
    const dialogEl = this.el.nativeElement.closest('.p-dialog')

    if (dialogEl) {
      const header = dialogEl.querySelector('.p-dialog-header')

      if (header) {
        const closeBtn = this.renderer.createElement('button')
        this.renderer.setAttribute(closeBtn, 'type', 'button')
        this.renderer.addClass(closeBtn, 'p-dialog-header-icon')
        this.renderer.addClass(closeBtn, 'p-dialog-header-close')
        this.renderer.addClass(closeBtn, 'p-link')

        const icon = this.renderer.createElement('span')
        this.renderer.addClass(icon, 'pi')
        this.renderer.addClass(icon, 'pi-times')
        this.renderer.appendChild(closeBtn, icon)

        this.renderer.listen(closeBtn, 'click', () => {
          this.onRequestClose()
        })

        this.renderer.appendChild(header, closeBtn)
      }
    }
  }

  private isTopmostDialog(): boolean {
    const myDialog = this.el.nativeElement.closest('.p-dialog')
    if (!myDialog) {
      return false
    }

    const allDialogs = document.querySelectorAll('.p-dialog')
    if (allDialogs.length <= 1) {
      return true
    }

    let topmostDialog: Element | null = null
    let highestZIndex = -1

    allDialogs.forEach((dialog) => {
      const zIndex = parseInt(window.getComputedStyle(dialog).zIndex || '0', 10)

      if (zIndex > highestZIndex) {
        highestZIndex = zIndex
        topmostDialog = dialog
      }
    })

    return myDialog === topmostDialog
  }

  ngOnDestroy(): void {
    this.$destroy.next(null)
    this.$destroy.complete()
  }

  onCloseRequested() {
    this.noMoreActions = true
    this.ref.close()
  }

  onClickButton(buttonId: string) {
    setTimeout(() => {
      if (!this.noMoreActions) {
        this.wizardService.clickButton(this.wizardExecutorId, buttonId, this.getProcessedFormValues())
      }
    })
  }

  onRequestClose() {
    setTimeout(() => {
      if (!this.noMoreActions) {
        this.wizardService.requestClose(this.wizardExecutorId, this.getProcessedFormValues())
      }
    })
  }

  trackByFunction(index, item) {
    return item.id // or any other unique property of items
  }

  openEntitySelectionDialog(contextId: string, dtoType: string, header: string) {
    this.entitySelectionHeader = header
    this.entitySelectionContextId = contextId
    this.entitySelectionDtoType = dtoType

    // let additionalQualifier = undefined;
    // if (wzField.referenceFilter) {
    //   additionalQualifier = new AdditionalQualifier(wzField.referenceFilter).setEntityRef(this.entityRef);
    // }
    // of(wzField.referenceSoftFilter).pipe(
    //   take(1),
    //   switchMap(filter => {
    //     if (filter?.includes('${')) {
    //       return this.entityService.eval(filter, undefined, this.entityRef).pipe(
    //         map(x => x.result)
    //       );
    //     } else {
    //       return of(filter);
    //     }
    //   })
    // ).subscribe(referenceSoftFilter => {
    //   this.entitySelectionTableData = new TableData(wzField.referenceType) //dtoType
    //     .isEntitySelectionDialog()
    //     .hideHeader()
    //     .hideAttachments()
    //     .setScrollable()
    //     .disableInlineEdit()
    //     .setScrollHeight(window.innerHeight - 350 + 'px')
    //     .setFilters(additionalQualifier, !!referenceSoftFilter ? new SoftFilter(this.entityRef, referenceSoftFilter) : undefined);
    //   this.entitySelectionVisible = true;
    // });

    this.entitySelectionTableData = new TableData(dtoType)
      .isEntitySelectionDialog()
      .hideHeader()
      .hideAttachments()
      .setScrollable()
      .disableInlineEdit()
      .setScrollHeight(window.innerHeight - 350 + 'px')
    this.entitySelectionVisible = true
  }

  entitySelected(entitySelected: any) {
    // ('entry selected:', entitySelected);
    this.wizardService.selectEntity(
      this.wizardExecutorId,
      this.entitySelectionContextId,
      this.entitySelectionDtoType,
      entitySelected.id
    )
    this.entitySelectionVisible = false
  }
}
